import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { IconShield } from '@tabler/icons-react';
import { functions } from '../firebase';
import { useAdminView } from '../contexts/AdminViewContext';
import './AdminVideosPage.css';

// Blind review of Claude's edits to the background videos (functions/src/video_pipeline.ts): for each ride Claude
// changed, the video after the correction tools and the video after Claude, in random order as A and B. The votes
// decide whether Claude's review is worth its cost (the scoreboard on /admin/videos).
const getQueue = httpsCallable(functions, 'getVideoReviewQueue');
const submitReview = httpsCallable(functions, 'submitVideoReview');
const clock = (t) => `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, '0')}`;

function AdminVideoReviewPage({ user }) {
  const { isAdmin } = useAdminView();
  const [items, setItems] = useState(null);
  const [index, setIndex] = useState(0);
  const [error, setError] = useState(null);
  const [note, setNote] = useState('');
  const [revealed, setRevealed] = useState(null);
  const [sending, setSending] = useState(false);
  const a = useRef(null);
  const b = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await getQueue({});
      // (each pair in random order, fixed for the session)
      setItems(res.data.items.map((it) => ({ ...it, claudeLeft: Math.random() < 0.5 })));
      setIndex(0);
    } catch (err) {
      setError(err.message || 'Could not load the videos to review.');
    }
  }, []);

  useEffect(() => { if (user && isAdmin) load(); }, [user, isAdmin, load]);

  const item = items && items[index];
  const [left, right] = useMemo(() => (item ? (item.claudeLeft ? [item.claude, item.tools] : [item.tools, item.claude]) : [null, null]), [item]);
  const both = (fn) => [a.current, b.current].forEach((v) => v && fn(v));
  const jump = (t) => both((v) => { v.currentTime = Math.max(0, t - 2); v.play().catch(() => {}); });
  const playBoth = () => both((v) => { v.currentTime = 0; v.play().catch(() => {}); });
  const pauseBoth = () => both((v) => v.pause());

  const vote = async (choice) => {
    if (!item || sending) return;
    const winner = choice === 'A' ? (item.claudeLeft ? 'claude' : 'tools') : choice === 'B' ? (item.claudeLeft ? 'tools' : 'claude') : choice;
    setSending(true);
    try {
      await submitReview({ pipelineId: item.id, winner, note, shownLeft: item.claudeLeft ? 'claude' : 'tools' });
      pauseBoth();
      setRevealed({ claude: item.claudeLeft ? 'A' : 'B', winner });
    } catch (err) {
      setError(err.message || 'Could not save the vote.');
    }
    setSending(false);
  };
  const next = () => { setRevealed(null); setNote(''); setIndex((i) => i + 1); };

  if (!user || !isAdmin) {
    return <div className="admin-videos-page"><div className="vp-denied"><IconShield size={48} /><h2>Admin access required</h2></div></div>;
  }

  return (
    <div className="admin-videos-page">
      <header className="vp-header">
        <Link to="/admin/videos" className="vp-back">← Background videos</Link>
        <h1>Review Claude's edits</h1>
        <p>Two versions of the same ride: one after the correction tools alone, one after Claude's review as well, in random order. Watch the places where they differ and pick the one that looks better. Which is which is shown after you vote.</p>
      </header>
      {error && <div className="vp-banner">{error}</div>}
      {items && items.length === 0 && <p className="vp-empty">Nothing to review: every ride Claude edited has your vote. New ones arrive as Claude edits more videos.</p>}
      {items && items.length > 0 && index >= items.length && (
        <p className="vp-empty">That's all {items.length} for now. <button type="button" className="vp-linkbutton" onClick={load}>Check for more</button></p>
      )}
      {item && (
        <section className="vr-pair" aria-label="The two versions">
          <p className="vp-meta">{index + 1} of {items.length} · {item.activity?.name} · {item.activity?.type} · {item.activity?.km} km · {item.activity?.place}</p>
          <div className="vr-videos">
            <figure><figcaption>A</figcaption><video ref={a} key={left} src={left} controls muted playsInline preload="auto" /></figure>
            <figure><figcaption>B</figcaption><video ref={b} key={right} src={right} controls muted playsInline preload="auto" /></figure>
          </div>
          <div className="vr-jumps">
            <button type="button" onClick={playBoth}>Play both from the start</button>
            <button type="button" onClick={pauseBoth}>Pause both</button>
            {(item.spots || []).filter((s) => s.t !== null).map((s, i) => (
              <button type="button" key={i} onClick={() => jump(s.t)}>Where they differ: {clock(s.t)} <span>({Math.abs(s.dy).toFixed(1)} m)</span></button>
            ))}
            {(!item.spots || item.spots.length === 0) && <span className="vp-meta">No jump points for this one: watch them through.</span>}
          </div>
          {!revealed ? (
            <>
              <div className="vr-vote" role="group" aria-label="Your vote">
                <button type="button" disabled={sending} onClick={() => vote('A')}>A looks better</button>
                <button type="button" disabled={sending} onClick={() => vote('B')}>B looks better</button>
                <button type="button" disabled={sending} onClick={() => vote('same')}>No visible difference</button>
                <button type="button" disabled={sending} onClick={() => vote('both_bad')}>Both look bad</button>
              </div>
              <label className="vr-note" htmlFor="vr-note">Note (optional)
                <textarea id="vr-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What made the difference?" />
              </label>
            </>
          ) : (
            <div className="vr-reveal">
              <p><b>{revealed.claude}</b> was Claude's version. Your vote: {revealed.winner === 'claude' ? 'Claude better' : revealed.winner === 'tools' ? 'the tools better' : revealed.winner === 'same' ? 'no visible difference' : 'both bad'}.</p>
              <button type="button" onClick={next}>Next</button>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

export default AdminVideoReviewPage;
